import { Text, useStyleConfig } from '@tectonic/elemason-components';
import {
  ProductReviewVerificationStatus,
  type ElemasonProductReviewsWidget,
  type ProductReview,
} from '@tectonic/types';
import clsx from 'clsx';
import { isEmpty, isNil } from 'lodash-es';
import { AssetList } from '../../Asset';
import { Icon } from '../../Icon';

import type { FC } from 'react';

const nthNumber = (num: number) => {
  if (num > 3 && num < 21) return 'th';
  switch (num % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

type ProductReviewCardProps = {
  config?: ElemasonProductReviewsWidget['config'];
  data?: ProductReview;
  icon: string;
};

const ProductReviewCard: FC<ProductReviewCardProps> = ({
  data: review,
  config,
  icon,
}) => {
  if (isEmpty(review)) {
    return null;
  }

  const { style: reviewCardContStyle, className: reviewCardContClassName } =
    useStyleConfig(config?.reviewCard?.container ?? {});

  const { style: reviewRatingContStyle, className: reviewRatingContClassName } =
    useStyleConfig(config?.reviewCard?.rating?.container ?? {});

  const {
    style: reviewRatingTextContStyle,
    className: reviewRatingTextContClassName,
  } = useStyleConfig(config?.reviewCard?.rating?.text?.container ?? {});

  const {
    style: reviewRatingIconContStyle,
    className: reviewRatingIconContClassName,
  } = useStyleConfig(config?.reviewCard?.rating?.icon?.container ?? {});

  const { style: reviewTitleContStyle, className: reviewTitleContClassName } =
    useStyleConfig(config?.reviewCard?.title?.container ?? {});

  const { style: reviewBodyContStyle, className: reviewBodyContClassName } =
    useStyleConfig(config?.reviewCard?.description?.container ?? {});

  const {
    user: userConf,
    reviewDate: reviewDateConf,
    seperator: sepConf,
    container: reviewInfoContConf,
  } = config?.reviewCard?.reviewInfo || {};

  const { style: reviewInfoContStyle, className: reviewInfoContClassName } =
    useStyleConfig(reviewInfoContConf ?? {});

  const { style: reviewUserContStyle, className: reviewUserContClassName } =
    useStyleConfig(userConf?.container ?? {});

  const { style: reviewDateContStyle, className: reviewDateContClassName } =
    useStyleConfig(reviewDateConf?.container ?? {});

  const { style: reviewInfoSepStyle, className: reviewInfoSepClassName } =
    useStyleConfig(sepConf?.container ?? {});

  const { style: verifiedContStyle, className: verifiedContClassName } =
    useStyleConfig(config?.reviewCard?.verification?.container ?? {});

  const { style: verifiedTextContStyle, className: verifiedTextContClassName } =
    useStyleConfig(config?.reviewCard?.verification?.text?.container ?? {});

  const { style: verifiedIconContStyle, className: verifiedIconContClassName } =
    useStyleConfig(config?.reviewCard?.verification?.icon?.container ?? {});

  let reviewHumanizedDay = '';

  // TODO move this logic outside of component if needed
  if (!isNil(review.updatedAt)) {
    const reviewDateObj = new Date(review.updatedAt);
    const reviewDay = reviewDateObj.getDate();
    const reviewMonth = reviewDateObj.toLocaleString('default', {
      month: 'long',
    });
    const reviewYear = reviewDateObj.getFullYear();
    reviewHumanizedDay = `${reviewDay}${nthNumber(
      reviewDay
    )} ${reviewMonth}, ${reviewYear}`;
  }

  const shouldShowSeperator =
    !isEmpty(review.userIdentifier) && !isEmpty(review.updatedAt);

  const isVerified =
    review.verificationStatus &&
    review.verificationStatus === ProductReviewVerificationStatus.VERIFIED;

  return (
    <div
      className={clsx('flex flex-col', reviewCardContClassName)}
      style={reviewCardContStyle}
    >
      <div
        className={clsx('flex flex-row', reviewRatingContClassName)}
        style={reviewRatingContStyle}
      >
        <div
          className={clsx('flex flex-col', reviewRatingTextContClassName)}
          style={reviewRatingTextContStyle}
        >
          <Text
            data={review.defaultRating.score.toString() || ''}
            config={config?.reviewCard?.rating?.text?.text}
          />
        </div>
        <div
          className={clsx('flex flex-col', reviewRatingIconContClassName)}
          style={reviewRatingIconContStyle}
        >
          <Icon symbol={icon} config={config?.reviewCard?.rating?.icon?.icon} />
        </div>
      </div>
      <div
        className={clsx('flex flex-col', reviewTitleContClassName)}
        style={reviewTitleContStyle}
      >
        <Text
          data={review.title || ''}
          config={config?.reviewCard?.title?.text}
        />
      </div>
      <div
        className={clsx('flex flex-col', reviewBodyContClassName)}
        style={reviewBodyContStyle}
      >
        <Text
          data={review.body || ''}
          config={config?.reviewCard?.description?.text}
        />
      </div>
      <div className="flex flex-col">
        <AssetList
          assets={review.assets || []}
          config={config?.reviewCard?.assetsList}
        />
      </div>
      <div className="flex flex-col">
        <div className="flex flex-row">
          <div
            className={clsx('flex flex-row', reviewInfoContClassName)}
            style={{ ...reviewInfoContStyle, flex: 1 }}
          >
            <div
              className={clsx('flex flex-col', reviewUserContClassName)}
              style={reviewUserContStyle}
            >
              <Text
                data={review.userIdentifier || ''}
                config={userConf?.text}
              />
            </div>
            {shouldShowSeperator && (
              <div
                className={clsx('flex flex-col', reviewInfoSepClassName)}
                style={reviewInfoSepStyle}
              >
                <Text data="•" config={sepConf?.text} />
              </div>
            )}
            <div
              className={clsx('flex flex-col', reviewDateContClassName)}
              style={reviewDateContStyle}
            >
              <Text data={reviewHumanizedDay} config={reviewDateConf?.text} />
            </div>
          </div>
          {isVerified && (
            <div
              className={clsx('flex flex-row', verifiedContClassName)}
              style={verifiedContStyle}
            >
              <div
                className={clsx('flex flex-col', verifiedIconContClassName)}
                style={verifiedIconContStyle}
              >
                <Icon
                  symbol="solid-verified"
                  config={config?.reviewCard?.verification?.icon?.icon}
                />
              </div>
              <div
                className={clsx('flex flex-col', verifiedTextContClassName)}
                style={verifiedTextContStyle}
              >
                <Text
                  data="Verified"
                  config={config?.reviewCard?.verification?.text?.text}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { ProductReviewCard };
